import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Modal,
  Form,
  message,
  Typography,
  Button,
} from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import dayjs, { Dayjs } from "dayjs";
import type { Booking } from "../../types/booking";
import type { Company } from "../../types/company";
import {
  bookingHasCommercialInvoice,
  getLeanCustomsReference,
} from "../../utils/bookingUtils";
import {
  buildOrderCostsAttributes,
  buildTransportCostDetailsLines,
  getPrimaryTransportCostTypeForEstimate,
  LEANCUSTOMS_ORDER_COST_LABELS_NL,
  mapBookingTransportToLeanCustoms,
  orderRequiresMainShippingEstimate,
} from "../../utils/leancustomsOrderCosts";
import {
  calculateLeanCustomsFreightCostsToEu,
  LEANCUSTOMS_EXPORT_CLEARANCE_COST,
} from "../../utils/leanCustomsFreightCosts";
import {
  fetchFclOriginThcOptions,
  pickOriginThcForContainerSize,
  type FclOriginThcOption,
} from "../../services/leanCustomsFclOriginThc";
import { getEstimatedTransportCostForBooking, type EstimatedTransportResult } from "../../services/pricingCalculationService";
import { LeanCustomsService } from "../../services/leancustomsService";
import {
  bookingNeedsCjkPackingSlipFont,
  ensurePackingSlipForBooking,
  getExistingPackingSlipStoragePath,
} from "../../services/packingSlipService";
import { getOtherDocuments } from "../../services/otherDocumentsService";
import {
  prepareLeanCustomsAttachments,
  type BookingOtherDocumentRow,
} from "../../services/leanCustomsAttachmentService";
import { supabase } from "../../lib/supabase";
import {
  isLeanCustomsStep1ReadyForCreate,
} from "./LeanCustomsWizardReadiness";
import { LeanCustomsCreateOrderFormSections } from "./LeanCustomsCreateOrderFormSections";
import { LeanCustomsCreateOrderProgress } from "./LeanCustomsCreateOrderProgress";
import {
  buildInitialCreateSteps,
  documentUploadDetail,
  attachmentUploadDetail,
  patchCreateStep,
  type LeanCustomsCreateStep,
  type LeanCustomsCreateStepId,
} from "./leanCustomsCreateOrderSteps";
import {
  LEANCUSTOMS_AGENT_ID,
  LEANCUSTOMS_EFREIGHT_ORG_ID,
} from "../../constants/leancustoms";
import { getCountryCode } from "../../utils/countryMapping";
import {
  getContainersFromBooking,
  getDefaultLeanCustomsDocumentNumberFromBooking,
  getMasterAwbReferenceFromBooking,
} from "../../utils/agentContainerMasterUtils";
import { getMasterLinkForContainer } from "../../services/masterContainerDocumentsService";

interface CreateLeanCustomsModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  booking: Booking;
  company: Company;
}

/** Let React paint step updates before heavy main-thread work (e.g. PDF generation). */
function yieldToMainThread(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      setTimeout(resolve, 0);
    });
  });
}

/** Normalize product_info (array or array-of-arrays) to Product[] */
function getProducts(booking: Booking): { gross_weight: number; value: number; cbm: number; cartons: number }[] {
  const raw = booking.product_info;
  if (!raw) return [];
  const arr = Array.isArray(raw) && raw.length > 0 && Array.isArray(raw[0]) ? raw[0] : raw;
  return (Array.isArray(arr) ? arr : []).map((p: any) => ({
    gross_weight: Number(p.gross_weight) || 0,
    value: Number(p.value) || 0,
    cbm: Number(p.cbm) || 0,
    cartons: Number(p.cartons) || 0,
  }));
}

/** ISO 3166-1 alpha-2 from port info (Dexwork requires 2-letter codes). */
function getCountryIso(portInfo: any): string {
  if (!portInfo) return "NL";
  const p = Array.isArray(portInfo) ? portInfo[0] : portInfo;
  return getCountryCode((p?.country as string) || "NL");
}

/** Normalize port info: can be object, array, or undefined */
function asPortInfo(v: any): any {
  if (!v) return null;
  if (Array.isArray(v)) return v[0] ?? null;
  if (typeof v === "object" && (v.name != null || v.country != null)) return v;
  return v;
}

/** Get ports from container, fallback to booking */
function getPortsFromContainer(booking: Booking): { originPortInfo: any; destinationPortInfo: any } {
  if (booking.container_info) {
    const containers = Array.isArray(booking.container_info) ? booking.container_info : [booking.container_info];
    const container = containers[0]; // Use first container
    if (container) {
      const o = asPortInfo(container.container_origin_port || container.origin_port_info);
      const d = asPortInfo(container.container_destination_port || container.destination_port_info);
      if (o || d) {
        return { originPortInfo: o, destinationPortInfo: d };
      }
    }
  }
  // Fallback to booking ports (detail shape: origin_port_info; list shape: origin_port as object)
  const originPortInfo = asPortInfo(booking.origin_port_info) ?? asPortInfo(booking.origin_port);
  const destinationPortInfo = asPortInfo(booking.destination_port_info) ?? asPortInfo(booking.destination_port);
  return {
    originPortInfo,
    destinationPortInfo,
  };
}

/** Extract container references from booking */
function getContainerReferences(booking: Booking): string[] {
  if (!booking.container_info) return [];
  const containers = Array.isArray(booking.container_info) ? booking.container_info : [booking.container_info];
  return containers
    .map((c: any) => c?.reference)
    .filter((ref: string | null | undefined): ref is string => !!ref);
}

/** Default AWB/B/L number: master ref (embedded or fetched), then container reference. */
async function resolveDefaultDocumentNumber(booking: Booking): Promise<string | null> {
  const fromBooking = getMasterAwbReferenceFromBooking(booking);
  if (fromBooking) return fromBooking;

  for (const container of getContainersFromBooking(booking)) {
    if (!container.id) continue;
    const result = await getMasterLinkForContainer(container.id);
    const ref = result.data?.masterReference?.trim();
    if (ref) return ref;
  }

  return getContainerReferences(booking)[0] ?? null;
}

/** Resolve container_size FK from booking join / containers row (FCL). */
function resolveBookingContainerSizeId(booking: Booking): number | null {
  if (booking.booking_type !== 2) return null;
  if (booking.container_info) {
    const containerInfo = Array.isArray(booking.container_info)
      ? booking.container_info[0]
      : booking.container_info;
    const fromJoin = (containerInfo as { container_size?: number | null })?.container_size;
    if (typeof fromJoin === "number") return fromJoin;
  }
  return null;
}

/** Build rich details string for Lean Customs order (everything useful for customs party) */
function buildOrderDetails(args: {
  bookingRef: string;
  transportName: string;
  bookingTypeName: string;
  incotermCode: string;
  originPort: string;
  destPort: string;
  originCountry: string;
  destCountry: string;
  grossWeight: number;
  netWeight: number;
  totalVolume: number;
  totalCartons: number;
  invoiceAmount: number;
  containerRefs: string[];
  containerEtd: string | null;
  containerEta: string | null;
  containerSize: string;
  confirmedPickUp: string | null;
  originPickupDate: string | null;
  pickupFee: number | null;
  exportClearance: boolean | null;
  companyName: string;
  companyVatNumber?: string | null;
  companyEoriNumber?: string | null;
  supplierName: string;
  commercialInvoiceFiles: string[];
  notes: string | null;
  status: string | null;
  eta: string | null;
  createdAt: string | null;
  productCount?: number;
  transportCostLines?: string[];
}): string {
  const line = (label: string, value: string | number | null | undefined) =>
    value != null && String(value).trim() !== "" ? `${label}: ${value}` : null;
  const lines: string[] = [];

  lines.push(`Reference: ${args.bookingRef}`);
  lines.push(`Transport: ${args.transportName}${args.bookingTypeName !== "—" ? ` (${args.bookingTypeName})` : ""}`);
  lines.push(`Incoterm: ${args.incotermCode}`);
  lines.push(`Origin: ${args.originPort}${args.originCountry && args.originCountry !== "—" ? `, ${args.originCountry}` : ""}`);
  lines.push(`Destination: ${args.destPort}${args.destCountry && args.destCountry !== "—" ? `, ${args.destCountry}` : ""}`);
  lines.push(`Gross weight: ${args.grossWeight} kg | Net weight: ${args.netWeight} kg | CBM: ${args.totalVolume} | Total cartons: ${args.totalCartons}`);
  lines.push(`Invoice amount: €${args.invoiceAmount}`);
  if (args.productCount != null && args.productCount > 0) {
    lines.push(`Product lines: ${args.productCount}`);
  }

  if (args.containerRefs.length > 0) {
    const parts = [`Containers: ${args.containerRefs.join(", ")}`];
    if (args.containerEtd) parts.push(`ETD: ${args.containerEtd}`);
    if (args.containerEta) parts.push(`ETA: ${args.containerEta}`);
    if (args.containerSize !== "—") parts.push(`Size: ${args.containerSize}`);
    lines.push(parts.join(" | "));
  }

  const d1 = line("Confirmed pick-up", args.confirmedPickUp);
  if (d1) lines.push(d1);
  const d2 = line("Origin pickup date", args.originPickupDate);
  if (d2) lines.push(d2);

  if (args.pickupFee != null || args.exportClearance != null) {
    const pf = args.pickupFee != null ? `€${args.pickupFee}` : "—";
    const ec = args.exportClearance === true ? "yes" : args.exportClearance === false ? "no" : "—";
    lines.push(`Pick-up fee: ${pf} | Export clearance: ${ec}`);
  }

  if (args.companyName && args.companyName !== "—") lines.push(`Company: ${args.companyName}`);
  const companyVat = line("VAT", args.companyVatNumber);
  if (companyVat) lines.push(companyVat);
  const companyEori = line("EORI", args.companyEoriNumber);
  if (companyEori) lines.push(companyEori);
  if (args.supplierName && args.supplierName !== "—") lines.push(`Supplier: ${args.supplierName}`);
  if (args.commercialInvoiceFiles.length > 0) {
    lines.push(`Commercial invoices: ${args.commercialInvoiceFiles.join(", ")}`);
  }

  const d3 = line("Status", args.status);
  if (d3) lines.push(d3);
  const d4 = line("Customer ETA", args.eta);
  if (d4) lines.push(d4);
  const d5 = line("Booking created", args.createdAt ? args.createdAt.split("T")[0] : null);
  if (d5) lines.push(d5);

  if (args.transportCostLines && args.transportCostLines.length > 0) {
    lines.push(...args.transportCostLines);
  }

  if (args.notes && args.notes.trim() !== "") {
    lines.push("---");
    lines.push("Notes:");
    lines.push(args.notes.trim());
  }

  return lines.join("\n");
}

export const CreateLeanCustomsModal: React.FC<CreateLeanCustomsModalProps> = ({
  open,
  onCancel,
  onSuccess,
  booking,
  company,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [createSteps, setCreateSteps] = useState<LeanCustomsCreateStep[] | null>(null);
  const [extraAttachmentList, setExtraAttachmentList] = useState<UploadFile[]>([]);
  const [otherDocuments, setOtherDocuments] = useState<BookingOtherDocumentRow[]>([]);
  const [otherDocumentsLoading, setOtherDocumentsLoading] = useState(false);
  const [selectedOtherDocumentIds, setSelectedOtherDocumentIds] = useState<number[]>([]);
  const [estimatedTransport, setEstimatedTransport] = useState<EstimatedTransportResult | null>(null);
  const [estimatedTransportLoading, setEstimatedTransportLoading] = useState(false);
  const [fclOriginThcOptions, setFclOriginThcOptions] = useState<FclOriginThcOption[]>([]);
  const [fclOriginThcLoading, setFclOriginThcLoading] = useState(false);

  useEffect(() => {
    if (!open || !booking) return;
    setEstimatedTransportLoading(true);
    setEstimatedTransport(null);
    getEstimatedTransportCostForBooking(booking)
      .then((result) => {
        setEstimatedTransport(result);
        // Log if no tariff was found for debugging
        if (result.source === "none") {
          console.warn("No tariff found for booking", {
            bookingId: booking.id,
            transport_type: booking.transport_type,
            origin_port: booking.origin_port,
            destination_port: booking.destination_port,
            etd: Array.isArray(booking.container_info)
              ? booking.container_info[0]?.etd
              : (booking.container_info as { etd?: string } | null)?.etd,
            confirmed_pick_up_date: booking.confirmed_pick_up_date,
          });
        }
      })
      .catch((error) => {
        console.error("Error getting estimated transport cost:", error);
        setEstimatedTransport({ amount: 0, currency: "EUR", source: "none" } as EstimatedTransportResult);
      })
      .finally(() => setEstimatedTransportLoading(false));
  }, [open, booking]);

  useEffect(() => {
    if (!open || !booking) return;
    const isFclSea = booking.booking_type === 2 && booking.transport_type === 2;
    if (!isFclSea) {
      setFclOriginThcOptions([]);
      setFclOriginThcLoading(false);
      return;
    }

    let cancelled = false;
    setFclOriginThcLoading(true);
    setFclOriginThcOptions([]);

    const matchDate = (() => {
      if (booking.container_info) {
        const container = Array.isArray(booking.container_info)
          ? booking.container_info[0]
          : booking.container_info;
        if (container?.etd) return container.etd;
      }
      if (booking.confirmed_pick_up_date) {
        const d = new Date(booking.confirmed_pick_up_date);
        if (!isNaN(d.getTime())) return d.toISOString().split("T")[0];
      }
      return new Date().toISOString().split("T")[0];
    })();

    fetchFclOriginThcOptions({
      matchDate,
      preferredIncotermId: booking.booking_incoterm ?? null,
    })
      .then((options) => {
        if (!cancelled) setFclOriginThcOptions(options);
      })
      .catch((error) => {
        console.error("Error loading FCL origin THC:", error);
        if (!cancelled) setFclOriginThcOptions([]);
      })
      .finally(() => {
        if (!cancelled) setFclOriginThcLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, booking]);

  useEffect(() => {
    if (!open || !booking?.id) {
      setOtherDocuments([]);
      setSelectedOtherDocumentIds([]);
      return;
    }
    let cancelled = false;
    setOtherDocumentsLoading(true);
    getOtherDocuments(booking.id)
      .then((result) => {
        if (cancelled) return;
        const docs = (result.data ?? []) as BookingOtherDocumentRow[];
        setOtherDocuments(docs);
        setSelectedOtherDocumentIds([]);
      })
      .finally(() => {
        if (!cancelled) setOtherDocumentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, booking?.id]);

  const orgId = company.leancustoms_organization_id || "";
  const bookingRef = getLeanCustomsReference(booking);
  const products = useMemo(() => getProducts(booking), [booking]);
  const grossWeight = products.reduce((s, p) => s + p.gross_weight, 0);
  const totalVolume = products.reduce((s, p) => s + p.cbm, 0);
  const totalCartons = products.reduce((s, p) => s + p.cartons, 0);
  const netWeight = Math.round(grossWeight * 0.9 * 100) / 100;
  const invoiceAmount = products.reduce((s, p) => s + p.value, 0);
  const transportMode = mapBookingTransportToLeanCustoms(booking);
  const isAir = transportMode.toLowerCase() === "air";
  const isSeafreight = transportMode.toLowerCase() === "maritime";
  const isFCL = booking.booking_type === 2;
  /** consolidation_level: air → always LCL; sea FCL → full_load; sea LCL → partial_load */
  const defaultConsolidationLevel = isAir
    ? "partial_load"
    : isSeafreight && isFCL
      ? "full_load"
      : isSeafreight
        ? "partial_load"
        : "full_load";
  const volumeWeight = totalVolume * 167;
  const chargeableWeight = Math.max(grossWeight, volumeWeight);
  const incotermName = (booking.booking_incoterm_info as any)?.[0]?.name ?? (booking.booking_incoterm_info as any)?.name ?? "";
  const incotermCode = (incotermName && /^[A-Z]{3}$/.test(incotermName))
    ? incotermName
    : (incotermName && incotermName.length >= 3 ? incotermName.substring(0, 3).toUpperCase() : "DAP");
  // Get ports from container, fallback to booking
  const containerPorts = useMemo(() => getPortsFromContainer(booking), [booking]);
  const destCountry = getCountryIso(containerPorts.destinationPortInfo);
  const origCountry = getCountryIso(containerPorts.originPortInfo);
  const containerReferences = useMemo(() => getContainerReferences(booking), [booking]);
  const defaultDocumentNumber = useMemo(
    () => getDefaultLeanCustomsDocumentNumberFromBooking(booking),
    [booking]
  );
  
  // Helper to get port display: name, or country if no name, or "—"
  const getPortDisplay = (portInfo: any): string => {
    if (!portInfo) return "—";
    const p = Array.isArray(portInfo) ? portInfo[0] : portInfo;
    if (!p) return "—";
    if (p.name) return p.name;
    if (p.country) return p.country;
    return "—";
  };
  
  const getPortName = (portInfo: any): string => getPortDisplay(portInfo);
  
  // Helper to get transport type name
  const getTransportTypeName = (): string => {
    const transportInfo = Array.isArray(booking.transport_type_info) 
      ? booking.transport_type_info[0] 
      : booking.transport_type_info;
    return transportInfo?.name || "—";
  };
  
  // Helper to get booking type name
  const getBookingTypeName = (): string => {
    const bookingTypeInfo = Array.isArray(booking.booking_type_info) 
      ? booking.booking_type_info[0] 
      : booking.booking_type_info;
    return bookingTypeInfo?.name || "—";
  };
  
  // Get container size if available
  const getContainerSize = (): string => {
    if (booking.container_info) {
      const containers = Array.isArray(booking.container_info) ? booking.container_info : [booking.container_info];
      const container = containers[0];
      if (container?.container_size) {
        return `${container.container_size}ft`;
      }
    }
    return "—";
  };
  
  // Get date used for tariff lookup (departure/ETD first — matches getEstimatedTransportCostForBooking)
  const getTariffDate = (): string => {
    if (booking.container_info) {
      const containers = Array.isArray(booking.container_info) ? booking.container_info : [booking.container_info];
      const container = containers[0];
      if (container?.etd) {
        return container.etd;
      }
    }
    if (booking.confirmed_pick_up_date) {
      return booking.confirmed_pick_up_date;
    }
    return new Date().toISOString().split("T")[0] + " (today)";
  };
  
  // Get container ETA / ETD
  const getContainerETA = (): string | null => {
    if (booking.container_info) {
      const containers = Array.isArray(booking.container_info) ? booking.container_info : [booking.container_info];
      const container = containers[0];
      return container?.eta || null;
    }
    return null;
  };
  const getContainerETD = (): string | null => {
    if (booking.container_info) {
      const containers = Array.isArray(booking.container_info) ? booking.container_info : [booking.container_info];
      const container = containers[0];
      return container?.etd || null;
    }
    return null;
  };

  const containerETA = useMemo(() => getContainerETA(), [booking.container_info]);
  const containerETD = useMemo(() => getContainerETD(), [booking.container_info]);

  const companyName = company?.name ?? "—";
  const companyVatNumber = company?.vat_number ?? null;
  const companyEoriNumber = company?.eori_number ?? null;
  const supplierName = (() => {
    const raw = booking.supplier_info;
    if (!raw) return "—";
    const s = Array.isArray(raw) ? raw[0] : raw;
    return (s as { name?: string })?.name ?? "—";
  })();
  const commercialInvoiceFiles = booking.commercial_invoice_file
    ? booking.commercial_invoice_file.split(",").map((s) => s.trim()).filter(Boolean)
    : [];

  const formValues = Form.useWatch([], form) ?? {};
  const orderType = (formValues as Record<string, unknown>).order_type as string | undefined;
  const requiresShippingEstimate = orderRequiresMainShippingEstimate(
    incotermCode,
    (orderType as "import" | "export" | undefined) || "import",
    transportMode
  );

  const prevOrderTypeRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (orderType === prevOrderTypeRef.current) return;
    prevOrderTypeRef.current = orderType;
    if (orderType === "export") {
      form.setFieldsValue({ declaration_type: "EX" });
    } else if (orderType === "import") {
      form.setFieldsValue({ declaration_type: "IM" });
    }
  }, [orderType, form]);

  // Get override values or use calculated/fetched values
  // (freight costs recalculated below once FCL container size / origin THC are known)
  
  // Manual estimated shipping when no tariff: price × quantity (kg, cbm, or 1 for FCL)
  const manualEstimatedShipping = useMemo(() => {
    const v = formValues as Record<string, unknown>;
    if (isAir) {
      const rate = v.price_per_kg as number | undefined;
      if (rate == null || rate <= 0) return 0;
      return Math.round(chargeableWeight * rate * 100) / 100;
    }
    if (isSeafreight) {
      if (isFCL) {
        const rate = v.price_per_container as number | undefined;
        return rate != null && rate > 0 ? rate : 0;
      }
      const rate = v.price_per_cbm as number | undefined;
      if (rate == null || rate <= 0) return 0;
      return Math.round(totalVolume * rate * 100) / 100;
    }
    return 0;
  }, [formValues.price_per_kg, formValues.price_per_cbm, formValues.price_per_container, isAir, isSeafreight, isFCL, chargeableWeight, totalVolume]);

  const fclTariffOptions = estimatedTransport?.fclTariffOptions ?? [];
  const needsFclSizeChoice = isFCL && fclTariffOptions.length > 1;

  const selectedFclTariff = useMemo(() => {
    if (!needsFclSizeChoice) return null;
    const rawId = formValues.selected_fcl_container_size_id as number | null | undefined;
    if (rawId === undefined || rawId === null) return null;
    return fclTariffOptions.find((o) => o.containerSizeId === rawId) ?? null;
  }, [needsFclSizeChoice, formValues.selected_fcl_container_size_id, fclTariffOptions]);

  /** Container size for origin THC: form choice → booking → single tariff option. */
  const resolvedFclContainerSizeId = useMemo(() => {
    if (!isFCL || !isSeafreight) return null;
    const fromForm = formValues.selected_fcl_container_size_id as number | null | undefined;
    if (typeof fromForm === "number") return fromForm;
    const fromBooking = resolveBookingContainerSizeId(booking);
    if (fromBooking != null) return fromBooking;
    if (fclTariffOptions.length === 1 && typeof fclTariffOptions[0].containerSizeId === "number") {
      return fclTariffOptions[0].containerSizeId;
    }
    if (fclOriginThcOptions.length === 1) return fclOriginThcOptions[0].containerSizeId;
    return null;
  }, [
    isFCL,
    isSeafreight,
    formValues.selected_fcl_container_size_id,
    booking,
    fclTariffOptions,
    fclOriginThcOptions,
  ]);

  const originTerminalHandling = useMemo(
    () => pickOriginThcForContainerSize(fclOriginThcOptions, resolvedFclContainerSizeId),
    [fclOriginThcOptions, resolvedFclContainerSizeId]
  );

  const freightCostsBreakdown = useMemo(
    () =>
      calculateLeanCustomsFreightCostsToEu({
        pickupFee: booking.pick_up_fee,
        exportClearance: booking.export_clearance,
        isFclSeafreight: isFCL && isSeafreight,
        originTerminalHandling,
      }),
    [
      booking.pick_up_fee,
      booking.export_clearance,
      isFCL,
      isSeafreight,
      originTerminalHandling,
    ]
  );

  const freightCostsFromBooking = freightCostsBreakdown.amount;

  const effectiveFreightCosts = useMemo(() => {
    const override = formValues.freight_costs_override as number | undefined;
    return override !== undefined && override !== null ? override : freightCostsFromBooking;
  }, [formValues.freight_costs_override, freightCostsFromBooking]);

  const hasTariff =
    (estimatedTransport?.source === "tariff" &&
      ((estimatedTransport?.tariffRate ?? 0) > 0 || (estimatedTransport?.amount ?? 0) > 0)) ||
    (selectedFclTariff != null && selectedFclTariff.amount > 0);

  const [verifiedCommercialInvoice, setVerifiedCommercialInvoice] = useState(() =>
    bookingHasCommercialInvoice(booking)
  );

  useEffect(() => {
    if (!open) return;
    setVerifiedCommercialInvoice(bookingHasCommercialInvoice(booking));
    if (!booking.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("bookings")
        .select("commercial_invoice_file")
        .eq("id", booking.id)
        .single();
      if (cancelled) return;
      setVerifiedCommercialInvoice(
        bookingHasCommercialInvoice({
          commercial_invoice_file: (data?.commercial_invoice_file as string | null) ?? null,
        })
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [open, booking.id, booking.commercial_invoice_file]);

  const effectiveEstimatedShipping = useMemo(() => {
    const override = formValues.estimated_shipping_override as number | undefined;
    if (override !== undefined && override !== null) return override;
    if (selectedFclTariff) return selectedFclTariff.amount;
    if (estimatedTransport?.source === "tariff") return estimatedTransport?.amount ?? 0;
    return manualEstimatedShipping;
  }, [
    formValues.estimated_shipping_override,
    selectedFclTariff,
    estimatedTransport?.source,
    estimatedTransport?.amount,
    manualEstimatedShipping,
  ]);

  const previewOrderCosts = useMemo(() => {
    const orderTypeValue = (formValues.order_type as "import" | "export" | undefined) || "import";
    return buildOrderCostsAttributes({
      incotermCode,
      orderType: orderTypeValue,
      transportMode,
      freightCostsToEu: effectiveFreightCosts,
      estimatedShipping: effectiveEstimatedShipping,
    });
  }, [
    formValues.order_type,
    incotermCode,
    transportMode,
    effectiveFreightCosts,
    effectiveEstimatedShipping,
  ]);

  const shippingSource = useMemo((): "tariff" | "manual" | "override" | "none" => {
    const override = formValues.estimated_shipping_override as number | undefined;
    if (override !== undefined && override !== null) return "override";
    if (hasTariff) return "tariff";
    if (manualEstimatedShipping > 0) return "manual";
    return "none";
  }, [formValues.estimated_shipping_override, hasTariff, manualEstimatedShipping]);

  const transportCostLines = useMemo(() => {
    const orderTypeValue = (formValues.order_type as "import" | "export" | undefined) || "import";
    const manualRate = isAir
      ? (formValues.price_per_kg as number | undefined)
      : isFCL
        ? (formValues.price_per_container as number | undefined)
        : (formValues.price_per_cbm as number | undefined);
    return buildTransportCostDetailsLines({
      transportMode,
      isFCL,
      grossWeight,
      totalVolume,
      volumeWeight,
      chargeableWeight,
      orderCosts: previewOrderCosts,
      primaryCostType: getPrimaryTransportCostTypeForEstimate(
        incotermCode,
        orderTypeValue,
        transportMode
      ),
      shippingSource,
      tariffRate: estimatedTransport?.tariffRate,
      tariffUnit: estimatedTransport?.tariffUnit,
      manualRate,
      manualUnit: isAir ? "per_kg" : isFCL ? "per_container" : "per_cbm",
      pickupFee: booking.pick_up_fee,
      exportClearance: booking.export_clearance,
      freightCostsOverride: formValues.freight_costs_override as number | undefined,
      originTerminalHandling,
      exportClearanceIncludedInThc: freightCostsBreakdown.exportClearanceIncludedInThc,
    });
  }, [
    formValues.order_type,
    formValues.price_per_kg,
    formValues.price_per_cbm,
    formValues.price_per_container,
    formValues.freight_costs_override,
    incotermCode,
    transportMode,
    isFCL,
    isAir,
    grossWeight,
    totalVolume,
    volumeWeight,
    chargeableWeight,
    previewOrderCosts,
    shippingSource,
    estimatedTransport?.tariffRate,
    estimatedTransport?.tariffUnit,
    booking.pick_up_fee,
    booking.export_clearance,
    originTerminalHandling,
    freightCostsBreakdown.exportClearanceIncludedInThc,
  ]);

  const orderDetails = useMemo(
    () =>
      buildOrderDetails({
        bookingRef,
        transportName: getTransportTypeName(),
        bookingTypeName: getBookingTypeName(),
        incotermCode,
        originPort: getPortDisplay(containerPorts.originPortInfo) || "—",
        destPort: getPortDisplay(containerPorts.destinationPortInfo) || "—",
        originCountry: origCountry,
        destCountry,
        grossWeight,
        netWeight,
        totalVolume,
        totalCartons,
        invoiceAmount,
        containerRefs: containerReferences,
        containerEtd: containerETD,
        containerEta: containerETA,
        containerSize: getContainerSize(),
        confirmedPickUp: booking.confirmed_pick_up_date ?? null,
        originPickupDate: booking.origin_pickup_date ?? null,
        pickupFee: booking.pick_up_fee ?? null,
        exportClearance: booking.export_clearance ?? null,
        companyName,
        companyVatNumber,
        companyEoriNumber,
        supplierName,
        commercialInvoiceFiles,
        notes: booking.notes ?? null,
        status: booking.status ?? null,
        eta: booking.eta ?? null,
        createdAt: booking.created_at ?? null,
        productCount: products.length,
        transportCostLines,
      }),
    [
      booking,
      bookingRef,
      incotermCode,
      containerPorts,
      origCountry,
      destCountry,
      grossWeight,
      netWeight,
      totalVolume,
      totalCartons,
      invoiceAmount,
      containerReferences,
      containerETD,
      containerETA,
      companyName,
      companyVatNumber,
      companyEoriNumber,
      supplierName,
      commercialInvoiceFiles,
      products.length,
      transportCostLines,
    ]
  );

  const prevOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = !!open;
    if (!open) {
      setCreateSteps(null);
      setSubmitting(false);
      setExtraAttachmentList([]);
      setSelectedOtherDocumentIds([]);
      return;
    }
    if (!booking) return;
    if (!justOpened) return;

    let cancelled = false;

    const prefillForm = async () => {
      const documentNumber = await resolveDefaultDocumentNumber(booking);
      if (cancelled) return;

      form.setFieldsValue({
        order_type: "import",
        consolidation_level: defaultConsolidationLevel,
        estimated_arrival: containerETA ? dayjs(containerETA) : null,
        document_number: documentNumber ?? undefined,
        selected_fcl_container_size_id: undefined,
      });
    };

    void prefillForm();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    booking,
    form,
    defaultConsolidationLevel,
    defaultDocumentNumber,
    containerETA,
  ]);

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({ details: orderDetails });
  }, [open, orderDetails, form]);

  const step1Ready = isLeanCustomsStep1ReadyForCreate(formValues, verifiedCommercialInvoice, {
    requiresShippingEstimate,
    hasTariff,
    manualShipping: manualEstimatedShipping,
    estimatedTransportLoading,
  });

  const handleCreateOrder = async () => {
    if (!verifiedCommercialInvoice) {
      message.warning(
        "Upload a commercial invoice on this booking before creating a Lean Customs order."
      );
      return;
    }

    let steps = buildInitialCreateSteps();
    const advance = (
      id: LeanCustomsCreateStepId,
      patch: Partial<Pick<LeanCustomsCreateStep, "status" | "detail">>
    ) => {
      steps = patchCreateStep(steps, id, patch);
      setCreateSteps([...steps]);
    };

    try {
      await form.validateFields(["document_number"]);
      setCreateSteps(steps);
      setSubmitting(true);

      const companyId = company?.id;
      if (companyId == null || typeof companyId !== "number") {
        message.error("Company ID is required");
        return;
      }
      const values = form.getFieldsValue();
      const orderType = (values.order_type as "import" | "export") || "import";
      const orderPayload: Record<string, unknown> = {
        reference: bookingRef,
        booking_number: bookingRef,
        agent_id: LEANCUSTOMS_AGENT_ID,
        client_id: LEANCUSTOMS_EFREIGHT_ORG_ID,
        transport_mode: transportMode,
        order_type: orderType,
        consolidation_level: isAir ? "partial_load" : values.consolidation_level || defaultConsolidationLevel,
        incoterm_code: incotermCode,
        details: orderDetails,
      portbase_documentation: false,
      automatic_teams: true,
      shipments_attributes: [{ reference: bookingRef, declarant_id: orgId }],
    };
    
    // Add estimated arrival if provided
    const estimatedArrivalValue = values.estimated_arrival as Dayjs | string | null | undefined;
    if (estimatedArrivalValue) {
      const arrivalDate = dayjs.isDayjs(estimatedArrivalValue) 
        ? estimatedArrivalValue.format("YYYY-MM-DD")
        : typeof estimatedArrivalValue === "string"
        ? estimatedArrivalValue
        : null;
      if (arrivalDate) {
        orderPayload.estimated_arrival_at = arrivalDate;
      }
    }

    // Add transport equipments (container references) when we have containers
    if (containerReferences.length > 0) {
      orderPayload.transport_equipments_attributes = containerReferences.map((ref) => ({
        equipment_number: ref,
        equipment_type: "container",
      }));
    }

    // Consignments: document number = B/L reference (mandatory, editable)
    const documentTypeCode = transportMode.toLowerCase() === "air" ? "N703" : "N705";
    const docNum = typeof values.document_number === "string" ? values.document_number.trim() : "";
    if (docNum) {
      orderPayload.consignments_attributes = [{
        document_type_code: documentTypeCode,
        document_number: docNum,
      }];
    }

    const orderCosts = buildOrderCostsAttributes({
      incotermCode,
      orderType,
      transportMode,
      freightCostsToEu:
        values.freight_costs_override !== undefined && values.freight_costs_override !== null
          ? values.freight_costs_override
          : freightCostsFromBooking,
      estimatedShipping:
        values.estimated_shipping_override !== undefined && values.estimated_shipping_override !== null
          ? values.estimated_shipping_override
          : effectiveEstimatedShipping,
    });
    if (orderCosts.length > 0) {
      orderPayload.order_costs_attributes = orderCosts;
    }

      advance("create_order", {
        status: "active",
        detail: `Sending order ${bookingRef}…`,
      });
      const orderRes = await LeanCustomsService.createOrder(companyId, orderPayload);
      if (!orderRes.success || !orderRes.data?.data?.id) {
        advance("create_order", {
          status: "error",
          detail: orderRes.error || "Failed to create order",
        });
        message.error(orderRes.error || "Failed to create LeanCustoms order");
        return;
      }
      const createdOrderId = orderRes.data.data.id;
      advance("create_order", {
        status: "done",
        detail: `Order created (${createdOrderId})`,
      });

      if (orderCosts.length > 0) {
        advance("sync_costs", {
          status: "active",
          detail: orderCosts
            .map(
              (c) =>
                `${LEANCUSTOMS_ORDER_COST_LABELS_NL[c.cost_type]}: €${c.amount.toFixed(2)}`
            )
            .join(" · "),
        });
        const costsRes = await LeanCustomsService.syncOrderCosts(
          companyId,
          createdOrderId,
          orderCosts
        );
        const savedCount = (costsRes.created ?? 0) + (costsRes.updated ?? 0);
        if (!costsRes.success) {
          const failedDetail = costsRes.failed
            ?.map((f) => `${f.cost_type}: ${f.error}`)
            .join("; ");
          if (savedCount > 0) {
            advance("sync_costs", {
              status: "done",
              detail: `${savedCount} of ${orderCosts.length} cost line(s) saved; some failed`,
            });
            message.warning(
              `Saved ${savedCount} of ${orderCosts.length} cost line(s) in Dexwork. ${failedDetail || costsRes.error || ""}`
            );
          } else {
            advance("sync_costs", {
              status: "error",
              detail: costsRes.error || failedDetail || "Failed to sync costs",
            });
            message.warning(
              `Order created but costs were not saved in Dexwork: ${costsRes.error || failedDetail}`
            );
          }
        } else {
          const parts: string[] = [];
          if ((costsRes.created ?? 0) > 0) parts.push(`${costsRes.created} created`);
          if ((costsRes.updated ?? 0) > 0) parts.push(`${costsRes.updated} updated`);
          const savedDetail =
            parts.length > 0
              ? `${parts.join(", ")} cost line(s) saved`
              : `${orderCosts.length} cost line(s) saved`;
          advance("sync_costs", {
            status: "done",
            detail: savedDetail,
          });
        }
      } else {
        advance("sync_costs", {
          status: "skipped",
          detail: "No costs to send",
        });
      }

      advance("link_booking", { status: "active", detail: "Saving on this booking…" });
      const { error: saveOrderError } = await supabase
        .from("bookings")
        .update({ leancustoms_order_id: createdOrderId })
        .eq("id", booking.id);
      if (saveOrderError) {
        advance("link_booking", {
          status: "error",
          detail: saveOrderError.message,
        });
        message.warning(`Order created, but saving order ID to booking failed: ${saveOrderError.message}`);
      } else {
        advance("link_booking", { status: "done", detail: "Booking linked" });
      }

      const documents: Array<{ bucket: string; path: string }> = [];
      if (booking.commercial_invoice_file) {
        for (const p of booking.commercial_invoice_file.split(",").map((s) => s.trim()).filter(Boolean)) {
          documents.push({ bucket: "commercial_invoices", path: p });
        }
      }

      const existingPackingSlipPath = await getExistingPackingSlipStoragePath(booking);
      if (existingPackingSlipPath) {
        documents.push({ bucket: "packingslips", path: existingPackingSlipPath });
        advance("packing_slip", {
          status: "done",
          detail: existingPackingSlipPath.split("/").pop() ?? "Packing slip ready",
        });
      } else if (bookingNeedsCjkPackingSlipFont(booking)) {
        // Noto Sans SC subsetting blocks the UI for ~15s; finish the order first.
        advance("packing_slip", {
          status: "done",
          detail: "Generating in background (Chinese font) — will upload when ready",
        });
        const orderIdForPackingSlip = createdOrderId;
        window.setTimeout(() => {
          void (async () => {
            const packingSlipPath = await ensurePackingSlipForBooking(booking);
            if (!packingSlipPath) return;
            await LeanCustomsService.uploadOrderFiles(companyId, orderIdForPackingSlip, [
              { bucket: "packingslips", path: packingSlipPath },
            ]);
          })();
        }, 0);
      } else {
        await yieldToMainThread();
        advance("packing_slip", { status: "active", detail: "Generating packing slip PDF…" });
        await yieldToMainThread();
        const packingSlipPath = await ensurePackingSlipForBooking(booking);
        if (packingSlipPath) {
          documents.push({ bucket: "packingslips", path: packingSlipPath });
          advance("packing_slip", {
            status: "done",
            detail: packingSlipPath.split("/").pop() ?? "Packing slip ready",
          });
        } else {
          advance("packing_slip", {
            status: "error",
            detail: "Could not generate packing slip — continuing without it",
          });
        }
      }

      if (documents.length > 0) {
        advance("upload_documents", {
          status: "active",
          detail: documentUploadDetail(documents),
        });
        const uploadRes = await LeanCustomsService.uploadOrderFiles(
          companyId,
          createdOrderId,
          documents
        );
        if (uploadRes.failed?.length) {
          const detail = uploadRes.failed
            .map((f) => `${f.path.split("/").pop() ?? f.path}: ${f.error}`)
            .join("; ");
          advance("upload_documents", {
            status: "error",
            detail: `${uploadRes.uploaded ?? 0} uploaded, ${uploadRes.failed.length} failed. ${detail}`,
          });
          message.warning(
            `${uploadRes.uploaded ?? 0} document(s) uploaded; ${uploadRes.failed.length} failed. ${detail}`
          );
        } else {
          advance("upload_documents", {
            status: "done",
            detail: `${uploadRes.uploaded ?? documents.length} file(s) uploaded`,
          });
        }
      } else {
        advance("upload_documents", {
          status: "skipped",
          detail: "No invoice or packing slip to upload",
        });
      }

      const newAttachmentFiles = extraAttachmentList
        .map((entry) => entry.originFileObj as File | undefined)
        .filter((file): file is File => file != null);
      const hasAttachments =
        newAttachmentFiles.length > 0 || selectedOtherDocumentIds.length > 0;

      if (hasAttachments) {
        advance("upload_attachments", {
          status: "active",
          detail: "Saving to booking…",
        });

        const { documents: attachmentDocuments, savedToBookingCount, errors: saveErrors } =
          await prepareLeanCustomsAttachments({
            bookingId: booking.id,
            companyId,
            newFiles: newAttachmentFiles,
            existingOtherDocuments: otherDocuments,
            selectedOtherDocumentIds,
          });

        if (saveErrors.length > 0) {
          message.warning(`Some files could not be saved to booking: ${saveErrors.join("; ")}`);
        }

        if (attachmentDocuments.length === 0) {
          advance("upload_attachments", {
            status: "error",
            detail: saveErrors.join("; ") || "No attachments ready to upload",
          });
        } else {
          advance("upload_attachments", {
            status: "active",
            detail: attachmentUploadDetail(
              attachmentDocuments.map((d) => d.path.split("/").pop() ?? d.path)
            ),
          });
          const attachmentRes = await LeanCustomsService.uploadOrderFiles(
            companyId,
            createdOrderId,
            attachmentDocuments
          );
          if (attachmentRes.failed?.length) {
            const detail = attachmentRes.failed
              .map((f) => `${f.path.split("/").pop() ?? f.path}: ${f.error}`)
              .join("; ");
            advance("upload_attachments", {
              status: "error",
              detail: `${attachmentRes.uploaded ?? 0} uploaded, ${attachmentRes.failed.length} failed. ${detail}`,
            });
            message.warning(
              `${attachmentRes.uploaded ?? 0} attachment(s) uploaded; ${attachmentRes.failed.length} failed. ${detail}`
            );
          } else {
            const savedNote =
              savedToBookingCount > 0 ? ` · ${savedToBookingCount} saved to booking` : "";
            advance("upload_attachments", {
              status: "done",
              detail: `${attachmentRes.uploaded ?? attachmentDocuments.length} file(s) uploaded${savedNote}`,
            });
          }
        }
      } else {
        advance("upload_attachments", {
          status: "skipped",
          detail: "No extra bijlagen selected",
        });
      }

      advance("complete", { status: "done", detail: "All done" });
      await new Promise((resolve) => setTimeout(resolve, 500));

      message.success(
        `Lean Customs order created and linked to this booking. Order ID: ${createdOrderId}`
      );
      form.resetFields();
      setExtraAttachmentList([]);
      setSelectedOtherDocumentIds([]);
      onSuccess();
      onCancel();
    } catch (e: any) {
      if (e?.errorFields) return;
      const failedStep = steps.find((s) => s.status === "active");
      if (failedStep) {
        advance(failedStep.id, {
          status: "error",
          detail: e?.message || "Unexpected error",
        });
      }
      message.error(e?.message || "Failed to create order");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Create Lean Customs order"
      open={open}
      onCancel={submitting ? undefined : onCancel}
      closable={!submitting}
      maskClosable={!submitting}
      footer={
        submitting ? (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              Working… see steps above
            </Typography.Text>
          </div>
        ) : (
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8 }}>
            <Button onClick={onCancel}>Cancel</Button>
            <Button
              type="primary"
              onClick={handleCreateOrder}
              disabled={!step1Ready}
            >
              Create order in Lean Customs
            </Button>
          </div>
        )
      }
      width={1180}
      destroyOnClose
      styles={{
        body: {
          maxHeight: "82vh",
          overflowY: "auto",
          overflowX: "hidden",
          maxWidth: "100%",
        },
      }}
    >
      {createSteps ? (
        <LeanCustomsCreateOrderProgress steps={createSteps} />
      ) : (
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          order_type: "import",
          consolidation_level: defaultConsolidationLevel,
          estimated_arrival: containerETA ? dayjs(containerETA) : null,
          price_per_kg: undefined,
          price_per_cbm: undefined,
          price_per_container: undefined,
          details: undefined,
          document_number: defaultDocumentNumber ?? undefined,
        }}
      >
        <LeanCustomsCreateOrderFormSections
          form={form}
          booking={booking}
          bookingRef={bookingRef}
          verifiedCommercialInvoice={verifiedCommercialInvoice}
          formValues={formValues}
          hasTariff={hasTariff}
          manualEstimatedShipping={manualEstimatedShipping}
          estimatedTransportLoading={estimatedTransportLoading}
          requiresShippingEstimate={requiresShippingEstimate}
          isAir={isAir}
          isSeafreight={isSeafreight}
          isFCL={isFCL}
          transportMode={transportMode}
          incotermCode={incotermCode}
          defaultConsolidationLevel={defaultConsolidationLevel}
          containerPorts={containerPorts}
          origCountry={origCountry}
          destCountry={destCountry}
          grossWeight={grossWeight}
          invoiceAmount={invoiceAmount}
          totalVolume={totalVolume}
          orgId={orgId}
          chargeableWeight={chargeableWeight}
          volumeWeight={volumeWeight}
          estimatedTransport={estimatedTransport}
          effectiveEstimatedShipping={effectiveEstimatedShipping}
          effectiveFreightCosts={effectiveFreightCosts}
          freightCostsFromBooking={freightCostsFromBooking}
          freightCostsBreakdown={freightCostsBreakdown}
          fclOriginThcLoading={fclOriginThcLoading}
          originTerminalHandling={originTerminalHandling}
          resolvedFclContainerSizeId={resolvedFclContainerSizeId}
          previewOrderCosts={previewOrderCosts}
          orderDetails={orderDetails}
          extraAttachmentList={extraAttachmentList}
          onExtraAttachmentListChange={setExtraAttachmentList}
          otherDocuments={otherDocuments}
          otherDocumentsLoading={otherDocumentsLoading}
          selectedOtherDocumentIds={selectedOtherDocumentIds}
          onSelectedOtherDocumentIdsChange={setSelectedOtherDocumentIds}
          submitting={submitting}
          getPortDisplay={getPortDisplay}
          getTransportTypeName={getTransportTypeName}
          getPortName={getPortName}
          getTariffDate={getTariffDate}
          exportClearanceCost={LEANCUSTOMS_EXPORT_CLEARANCE_COST}
          onAttachmentTooLarge={(filename) =>
            message.error(`${filename} is too large (max 10 MB)`)
          }
        />
      </Form>
      )}
    </Modal>
  );
};

export default CreateLeanCustomsModal;
